import {
  eachDayOfInterval,
  isBefore,
  isSameDay,
  isSameMonth,
  lastDayOfMonth,
  parseISO,
  set,
} from 'date-fns'
import type { AbsenceDate, AbsenceNode } from './types.graphql'
import { useSuspenseQuery } from '@apollo/client'
import { ABSENCE_LOCKED_BEFORE_QUERY } from './queries'

/* AbsenceDate generation */

interface DatesWithInfo {
  info: string
  dates: Date[]
}

/**
 * Removes absences outside of the range given by month or rangeOverride
 * @param {AbsenceNode[]} nodes - Absence nodes to iterate over
 * @param {Date | null} monthStart - Start  of the selected range if the range is a month
 * @param {Date[] | undefined} rangeOverride - Overrides the month logic with a custom range
 * @returns {AbsenceDate} An array with objects containing date and absence reason.
 */

type ReducedAbsenceNode = Pick<
  AbsenceNode,
  'datetimeStart' | 'datetimeEnd' | 'absenceType' | 'reason'
>

export function generateAbsenceDates(
  absenceNodes: ReducedAbsenceNode[],
  monthStart: Date | null,
  rangeOverride?: Date[]
) {
  const cleanNodes = removeAbsencesOutsideRange(
    absenceNodes,
    monthStart,
    rangeOverride
  )

  const dateArrays: DatesWithInfo[] = cleanNodes.map(node => {
    const trimmedRange = rangeFromNode(node, monthStart, rangeOverride)

    return {
      info: (node.absenceType?.name ?? 'Unknown') + ': ' + node.reason,
      dates: eachDayOfInterval({
        start: trimmedRange[0],
        end: trimmedRange[1],
      }),
    }
  })

  const dates: AbsenceDate[][] = dateArrays.map(datesAndText => {
    return datesAndText.dates.map(date => {
      return { date: date, reason: datesAndText.info }
    })
  })

  const mergedDates = ([] as AbsenceDate[]).concat.apply([], dates)

  const uniqueDates = mergedDates.filter(
    (absenceDate, i, arr) =>
      arr
        .slice(0, i)
        .filter(mapAbsence => isSameDay(absenceDate.date, mapAbsence.date))
        .length === 0
  )

  return uniqueDates
}

/**
 * Removes absence nodes outside of of range
 * @param {AbsenceNode[]} nodes - Absence nodes to iterate over
 * @param {Date | null} monthStart - Start  of the selected range if the range is a month
 * @param {Date[] | undefined} rangeOverride - Overrides the month logic with a custom range
 * @returns {absenceNode[]} Array containing absence nodes inside range
 */
function removeAbsencesOutsideRange(
  nodes: ReducedAbsenceNode[],
  monthStart: Date | null,
  rangeOverride?: Date[]
) {
  const rangeOverrideStart = rangeOverride && rangeOverride[0]
  const rangeOverrideEnd = rangeOverride && rangeOverride[1]
  if (!!rangeOverrideStart && !!rangeOverrideEnd) {
    return nodes.filter(
      node =>
        isBefore(rangeOverrideStart, parseISO(node.datetimeEnd)) &&
        isBefore(parseISO(node.datetimeStart), rangeOverrideEnd)
    )
  } else if (monthStart) {
    const monthEnd = lastDayOfMonth(monthStart)
    set(monthEnd, { hours: 23, minutes: 59 })
    return nodes.filter(
      node =>
        !(
          isBefore(parseISO(node.datetimeEnd), monthStart) &&
          !isBefore(monthEnd, parseISO(node.datetimeStart))
        )
    )
  }
  return nodes
}

/**
 * Generates a date array for the given AbsenceNode that overlaps the selected range
 * @param {AbsenceNode} node - Absence node to generate date array
 * @param {Date | null} monthStart - Start  of the selected range if the range is a month
 * @param {Date[] | undefined} rangeOverride - Overrides the month logic with a custom range
 * @returns {Date[]} Date array containing the range endpoints
 */
function rangeFromNode(
  node: ReducedAbsenceNode,
  monthStart: Date | null,
  rangeOverride?: Date[]
): [Date, Date] {
  const range: [Date, Date] = [
    parseISO(node.datetimeStart),
    parseISO(node.datetimeEnd),
  ]

  if (!!rangeOverride && !!rangeOverride[0] && !!rangeOverride[1]) {
    const rangeStart = isBefore(range[0], rangeOverride[0])
      ? rangeOverride[0]
      : range[0]
    const rangeEnd = isBefore(rangeOverride[1], range[1])
      ? rangeOverride[1]
      : range[1]

    return [rangeStart, rangeEnd]
  } else if (monthStart) {
    const monthEnd = lastDayOfMonth(monthStart)
    set(monthEnd, { hours: 23, minutes: 59 })

    const rangeStart =
      isBefore(range[0], monthStart) || !isSameMonth(range[0], monthStart)
        ? monthStart
        : range[0]
    const rangeEnd =
      isBefore(monthEnd, range[1]) || !isSameMonth(range[1], monthEnd)
        ? monthEnd
        : range[1]

    return [rangeStart, rangeEnd]
  }
  return [new Date(), new Date()]
}

export function timeStringToMinutes(timeString: string) {
  if (!/^\d{2}:\d{2}$/.test(timeString)) return 0
  const hours = parseInt(timeString.substring(0, 2))
  const minutes = parseInt(timeString.substring(3))
  return hours * 60 + minutes
}

/**
 * This function takes a in an array of WeekReports and the month and returns all time- and absenceEntries within
 * @param weeks collection of weeks
 * @param month Date object
 * @returns an object with time- and absenceEntries in it as such:
 * `{
 *    timeEntries: timeEntries[],
 *    absenceEntries: absenceEntries[]
 *  }`
 */
// Commented out because unused, but me be included again in the future
// export function getCurrentMonthEntriesFromWeekReports(
//   weeks: WeekReport[],
//   month: Date
// ) {
//   let timeEntries: Omit<TimeEntryNode, 'company'>[] = []
//   let absenceEntries: Omit<AbsenceNode, 'company'>[] = []
//   for (const week of weeks) {
//     for (const day of week.dayReports) {
//       if (month.getMonth() === new Date(day.date).getMonth()) {
//         timeEntries.push.apply(timeEntries, day.timeEntries)
//         absenceEntries.push.apply(absenceEntries, day.absenceEntries)
//       }
//     }
//   }
//   return { timeEntries, absenceEntries }
// }

interface MinuteInterval {
  start: number
  end: number
}
export function getMinuteIntervalOverlap(
  interval1: MinuteInterval,
  interval2: MinuteInterval
) {
  /**
   * Takes two minute intervals and calculate the overlap
   * @param interval1 The first interval
   * @param interval2 The second interval
   * @return The overlap in minutes
   */
  if (interval1.start >= interval2.end || interval2.start >= interval1.end)
    return 0
  if (interval1.start <= interval2.start && interval1.end >= interval2.end)
    return interval2.end - interval2.start
  if (interval2.start <= interval1.start && interval2.end >= interval1.end)
    return interval1.end - interval1.start
  if (interval1.start <= interval2.start && interval1.end <= interval2.end)
    return interval1.end - interval2.start
  if (interval2.start <= interval1.start && interval2.end <= interval1.end)
    return interval2.end - interval1.start
  return 0
}

export function dateFromStringWithoutTimezone(dateStr: string): Date {
  const [year, month, day] = (dateStr.split('T')[0] ?? '')
    .split('-')
    .map(Number)
  return new Date(year ?? 0, (month ?? 0) - 1, day)
}

export function getAbsenceLockActionState(lockActions: number) {
  return {
    createLocked: (lockActions & 1) !== 0,
    updateLocked: (lockActions & 2) !== 0,
    deleteLocked: (lockActions & 4) !== 0,
  }
}

export type RRuleFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'
export type RRuleWeekday = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU'

export const RRuleWeekdayValues: readonly RRuleWeekday[] = [
  'MO',
  'TU',
  'WE',
  'TH',
  'FR',
  'SA',
  'SU',
] as const

export function isRRuleWeekday(value: string): value is RRuleWeekday {
  return RRuleWeekdayValues.includes(value as RRuleWeekday)
}

export function isRRuleWeekdays(values: string[]): values is RRuleWeekday[] {
  return values.every(isRRuleWeekday)
}

export const RRuleWeekdayByIndex: Record<number, RRuleWeekday> = {
  0: 'SU',
  1: 'MO',
  2: 'TU',
  3: 'WE',
  4: 'TH',
  5: 'FR',
  6: 'SA',
}

export interface RRuleBuilderInput {
  /** Base recurrence frequency (RFC 5545 FREQ). */
  frequency: RRuleFrequency
  /** Interval between occurrences (RFC 5545 INTERVAL). Defaults to 1 when omitted. */
  interval?: number
  /** Weekdays for weekly/monthly rules (RFC 5545 BYDAY). */
  byWeekdays?: RRuleWeekday[]
  /** Months for yearly rules (RFC 5545 BYMONTH). Uses 1-12. */
  byMonths?: number[]
  /** Month days for monthly rules (RFC 5545 BYMONTHDAY). */
  byMonthDays?: number[]
  /** Set positions for monthly rules (RFC 5545 BYSETPOS). */
  bySetPos?: number[]
  /** Inclusive end date (RFC 5545 UNTIL), as Date or YYYY-MM-DD. */
  until?: Date | string
}

const WEEKDAY_ORDER: RRuleWeekday[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']

export function parseRRuleParts(rrule?: string | null) {
  const parts: Record<string, string> = {}
  rrule
    ?.split(';')
    .map(part => part.trim())
    .filter(Boolean)
    .forEach(part => {
      const [key, value] = part.split('=')
      if (!key || value === undefined) return
      parts[key.toUpperCase()] = value
    })

  const byWeekdays =
    parts.BYDAY?.split(',')
      .map(value => value.trim())
      .filter((value): value is RRuleWeekday =>
        RRuleWeekdayValues.includes(value as RRuleWeekday)
      ) ?? []

  const byMonthDays =
    parts.BYMONTHDAY?.split(',')
      .map(value => Number.parseInt(value, 10))
      .filter(value => Number.isFinite(value)) ?? []

  const byMonths =
    parts.BYMONTH?.split(',')
      .map(value => Number.parseInt(value, 10))
      .filter(value => Number.isFinite(value)) ?? []

  const interval = Number.parseInt(parts.INTERVAL ?? '1', 10)

  return {
    frequency: parts.FREQ,
    interval: Number.isFinite(interval) && interval > 0 ? interval : 1,
    byWeekdays,
    byMonthDays,
    byMonths,
  }
}

function formatDateForUntil(value: Date | string) {
  if (typeof value === 'string') {
    return value.replace(/-/g, '')
  }

  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}${month}${day}`
}

/**
 * Builds an RFC 5545 RRULE string from structured input.
 * Only emits parts that are supplied (defaults to FREQ + optional fields).
 */
export function buildRRule(input: RRuleBuilderInput) {
  const parts: string[] = [`FREQ=${input.frequency}`]

  if (input.interval && input.interval > 1) {
    parts.push(`INTERVAL=${input.interval}`)
  }

  if (input.byWeekdays && input.byWeekdays.length > 0) {
    const sortedWeekdays = [...new Set(input.byWeekdays)].sort(
      (a, b) => WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b)
    )
    parts.push(`BYDAY=${sortedWeekdays.join(',')}`)
  }

  if (input.byMonths && input.byMonths.length > 0) {
    const uniqueMonths = [...new Set(input.byMonths)].sort((a, b) => a - b)
    parts.push(`BYMONTH=${uniqueMonths.join(',')}`)
  }

  if (input.byMonthDays && input.byMonthDays.length > 0) {
    const uniqueDays = [...new Set(input.byMonthDays)].sort((a, b) => a - b)
    parts.push(`BYMONTHDAY=${uniqueDays.join(',')}`)
  }

  if (input.bySetPos && input.bySetPos.length > 0) {
    const uniquePositions = [...new Set(input.bySetPos)].sort((a, b) => a - b)
    parts.push(`BYSETPOS=${uniquePositions.join(',')}`)
  }

  if (input.until) {
    parts.push(`UNTIL=${formatDateForUntil(input.until)}`)
  }

  return parts.join(';')
}

export function useAbsenceLockedBefore() {
  const { data } = useSuspenseQuery(ABSENCE_LOCKED_BEFORE_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  })

  return {
    createLockedBefore: !!data.absenceLockedBefore.create
      ? new Date(data.absenceLockedBefore.create)
      : null,
    updateLockedBefore: !!data.absenceLockedBefore.update
      ? new Date(data.absenceLockedBefore.update)
      : null,
    deleteLockedBefore: !!data.absenceLockedBefore.delete
      ? new Date(data.absenceLockedBefore.delete)
      : null,
  }
}
